import { Injectable } from '@angular/core';

const FP_STORAGE_KEY = 'qrsto_fp';
const FP_COOKIE_NAME = 'qrsto_fp';
const FP_COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

@Injectable({
    providedIn: 'root'
})
export class FingerprintService {
    private _fingerprint: string | null = null;

    /**
     * Generate or retrieve a stable browser fingerprint hash.
     * Persists in memory, localStorage, and cookie for redundancy.
     */
    async generateFingerprint(): Promise<string> {
        // Return cached value if available
        if (this._fingerprint) {
            return this._fingerprint;
        }

        // Try to restore from localStorage or cookie
        const stored = this.getStoredFingerprint();
        if (stored) {
            this._fingerprint = stored;
            this.persist(stored);
            return stored;
        }

        // Generate a new fingerprint
        const signals = await this.collectSignals();
        const hash = await this.sha256(signals);

        this._fingerprint = hash;
        this.persist(hash);

        return hash;
    }

    /**
     * Collect browser signals for fingerprinting
     */
    private async collectSignals(): Promise<string> {
        const parts: string[] = [];

        // Canvas fingerprint
        parts.push(this.getCanvasFingerprint());

        // Screen properties
        parts.push(`${screen.width}x${screen.height}x${screen.colorDepth}`);

        // User agent
        parts.push(navigator.userAgent);

        // Language
        parts.push(navigator.language);

        // Timezone
        parts.push(Intl.DateTimeFormat().resolvedOptions().timeZone);

        // Platform
        parts.push(navigator.platform || '');

        // WebGL renderer
        parts.push(this.getWebGLRenderer());

        // Hardware concurrency
        parts.push(String(navigator.hardwareConcurrency || 0));

        // Device memory (if available)
        parts.push(String((navigator as any).deviceMemory || 0));

        return parts.join('|');
    }

    /**
     * Generate a canvas fingerprint by drawing text and shapes
     */
    private getCanvasFingerprint(): string {
        try {
            const canvas = document.createElement('canvas');
            canvas.width = 200;
            canvas.height = 50;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                return 'no-canvas';
            }

            // Draw text with specific font
            ctx.textBaseline = 'top';
            ctx.font = '14px Arial';
            ctx.fillStyle = '#f60';
            ctx.fillRect(125, 1, 62, 20);
            ctx.fillStyle = '#069';
            ctx.fillText('QRSto fp', 2, 15);
            ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
            ctx.fillText('QRSto fp', 4, 17);

            // Draw shapes
            ctx.beginPath();
            ctx.arc(50, 25, 10, 0, Math.PI * 2);
            ctx.fill();

            return canvas.toDataURL();
        } catch {
            return 'canvas-error';
        }
    }

    /**
     * Get WebGL renderer string
     */
    private getWebGLRenderer(): string {
        try {
            const canvas = document.createElement('canvas');
            const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
            if (!gl) {
                return 'no-webgl';
            }
            const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
            if (!debugInfo) {
                return 'no-debug-info';
            }
            return (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'unknown';
        } catch {
            return 'webgl-error';
        }
    }

    /**
     * SHA-256 hash using SubtleCrypto, with fallback for non-secure contexts (HTTP on IP)
     */
    private async sha256(message: string): Promise<string> {
        const msgBuffer = new TextEncoder().encode(message);

        // crypto.subtle requires a secure context (HTTPS or localhost)
        if (typeof crypto !== 'undefined' && crypto.subtle) {
            const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        }

        // Fallback: simple hash for non-secure contexts (e.g., HTTP on IP address)
        return this.simpleHash(message);
    }

    /**
     * Simple non-cryptographic hash fallback (DJB2 variant, 64-char hex output)
     */
    private simpleHash(str: string): string {
        let h1 = 0xdeadbeef;
        let h2 = 0x41c6ce57;
        for (let i = 0; i < str.length; i++) {
            const ch = str.charCodeAt(i);
            h1 = Math.imul(h1 ^ ch, 2654435761);
            h2 = Math.imul(h2 ^ ch, 1597334677);
        }
        h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
        h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
        const combined = (4294967296 * (2097151 & h2) + (h1 >>> 0));
        return combined.toString(16).padStart(16, '0').repeat(4).substring(0, 64);
    }

    /**
     * Retrieve fingerprint from localStorage or cookie
     */
    private getStoredFingerprint(): string | null {
        // Try localStorage
        try {
            const stored = localStorage.getItem(FP_STORAGE_KEY);
            if (stored && stored.length === 64) {
                return stored;
            }
        } catch {
            // localStorage may not be available
        }

        // Try cookie
        return this.getCookie(FP_COOKIE_NAME);
    }

    /**
     * Persist fingerprint to localStorage and cookie
     */
    private persist(hash: string): void {
        // localStorage
        try {
            localStorage.setItem(FP_STORAGE_KEY, hash);
        } catch {
            // Silently fail
        }

        // Cookie (1 year)
        this.setCookie(FP_COOKIE_NAME, hash, FP_COOKIE_MAX_AGE);
    }

    /**
     * Set a cookie with the given name, value, and max-age
     */
    private setCookie(name: string, value: string, maxAge: number): void {
        document.cookie = `${name}=${value};max-age=${maxAge};path=/;SameSite=Lax`;
    }

    /**
     * Get a cookie value by name
     */
    private getCookie(name: string): string | null {
        const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
        return match ? match[1] : null;
    }
}
